<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import { listAppLogs, appLogSummary, listDevicesLogs, ApiError } from "./api";
  import Autocomplete from "./Autocomplete.svelte";
  import { push } from "svelte-spa-router";
  import UserLink from "./UserLink.svelte";
  import type {
    LogEntryDTO,
    LogSummaryResponse,
    LogTargetType,
    DeviceLogInfo,
  } from "@diy-avatar/shared/admin";

  const USER_TARGET_TYPES = new Set(["student", "teacher", "admin", "kid"]);

  /** True when the entry targets a user-like type with a numeric id. */
  function isUserTarget(e: LogEntryDTO): boolean {
    return (
      USER_TARGET_TYPES.has(e.target_type) &&
      !!e.target_id &&
      /^\d+$/.test(e.target_id)
    );
  }

  // ---- filter state ----
  /** "" = unset (no target_type filter). */
  let targetType = "";
  let targetId = "";
  /** Level string sent to the API. "" sends nothing → backend default error,warn. */
  let level = "";
  let q = "";
  let sessionId = "";
  let fromDt = ""; // datetime-local
  let toDt = ""; // datetime-local
  let limit = 200;
  let autoRefresh = false;

  // ---- device list (for the target-id autocomplete) ----
  let devices: DeviceLogInfo[] = [];
  let devicesLoaded = false;
  $: deviceItems = devices.map((d) => ({ value: d.label, label: d.label }));

  // ---- results ----
  let entries: LogEntryDTO[] = [];
  let nextCursor: string | null = null;
  let summary: LogSummaryResponse | null = null;
  let loading = false;
  let error: string | null = null;
  let expanded: Record<number, boolean> = {};

  let refreshTimer: ReturnType<typeof setInterval> | null = null;

  const TARGET_TYPES: LogTargetType[] = [
    "server",
    "device",
    "student",
    "teacher",
    "admin",
    "kid",
  ];

  function handle401(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) {
      push("/");
      return true;
    }
    return false;
  }

  /** datetime-local string → epoch ms; undefined when empty/invalid. */
  function dtToMs(v: string): number | undefined {
    if (!v) return undefined;
    const ms = Date.parse(v);
    return Number.isNaN(ms) ? undefined : ms;
  }

  /** epoch ms → "YYYY-MM-DD HH:MM:SS" (local). */
  function fmtTs(ms: number): string {
    const d = new Date(ms);
    const p = (n: number) => String(n).padStart(2, "0");
    return (
      `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ` +
      `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
    );
  }

  function targetLabel(e: LogEntryDTO): string {
    return e.target_type + (e.target_id ? `:${e.target_id}` : "");
  }

  function levelClass(lvl: string): string {
    const l = lvl.toLowerCase();
    if (l === "error") return "lvl-error";
    if (l === "warn" || l === "warning") return "lvl-warn";
    return "lvl-muted";
  }

  function truncate(s: string, n = 160): string {
    return s.length > n ? s.slice(0, n) + "…" : s;
  }

  function prettyExtra(extra: unknown): string {
    if (extra === null || extra === undefined) return "";
    try {
      return JSON.stringify(extra, null, 2);
    } catch {
      return String(extra);
    }
  }

  /** Shared target/time filters for both list + summary queries. */
  function baseFilters(): {
    target_type?: LogTargetType;
    target_id?: string;
    since?: number;
    until?: number;
  } {
    const tt = targetType ? (targetType as LogTargetType) : undefined;
    return {
      target_type: tt,
      target_id: targetId || undefined,
      since: dtToMs(fromDt),
      until: dtToMs(toDt),
    };
  }

  async function loadDevices() {
    if (devicesLoaded) return;
    try {
      const res = await listDevicesLogs();
      devices = res.devices;
      devicesLoaded = true;
    } catch (err) {
      if (handle401(err)) return;
      // Non-fatal: fall back to the text input.
    }
  }

  async function loadSummary() {
    try {
      summary = await appLogSummary(baseFilters());
    } catch (err) {
      if (handle401(err)) return;
      // Summary is best-effort; keep the table usable.
      summary = null;
    }
  }

  /**
   * Load the first page (replace) and refresh the summary. Paging older entries
   * is done by passing the returned `next_cursor` back as `until` in loadMore().
   */
  async function load() {
    loading = true;
    error = null;
    try {
      const res = await listAppLogs({
        ...baseFilters(),
        level: level || undefined,
        q: q || undefined,
        session_id: sessionId || undefined,
        limit,
      });
      entries = res.entries;
      nextCursor = res.next_cursor;
      expanded = {};
      await loadSummary();
    } catch (err) {
      if (handle401(err)) return;
      error = err instanceof Error ? err.message : "Could not load logs.";
    } finally {
      loading = false;
    }
  }

  /** Append the next older page using next_cursor as the `until` bound. */
  async function loadMore() {
    if (nextCursor === null) return;
    loading = true;
    error = null;
    try {
      const res = await listAppLogs({
        ...baseFilters(),
        level: level || undefined,
        q: q || undefined,
        session_id: sessionId || undefined,
        limit,
        until: nextCursor,
      });
      entries = [...entries, ...res.entries];
      nextCursor = res.next_cursor;
    } catch (err) {
      if (handle401(err)) return;
      error = err instanceof Error ? err.message : "Could not load logs.";
    } finally {
      loading = false;
    }
  }

  function apply() {
    void load();
  }

  function reset() {
    targetType = "";
    targetId = "";
    level = "";
    q = "";
    sessionId = "";
    fromDt = "";
    toDt = "";
    limit = 200;
    void load();
  }

  function toggleRow(id: number) {
    expanded = { ...expanded, [id]: !expanded[id] };
  }

  /** Row filter icon: scope to this entry's target then reload. */
  function filterTarget(e: LogEntryDTO) {
    targetType = e.target_type;
    targetId = e.target_id ?? "";
    apply();
  }

  // Auto-refresh: re-run the current query every 8s while enabled.
  function syncAutoRefresh() {
    if (refreshTimer) {
      clearInterval(refreshTimer);
      refreshTimer = null;
    }
    if (autoRefresh) {
      refreshTimer = setInterval(() => void load(), 8000);
    }
  }
  $: autoRefresh, syncAutoRefresh();

  onMount(() => {
    void loadDevices();
    void load();
  });

  onDestroy(() => {
    if (refreshTimer) clearInterval(refreshTimer);
  });
</script>

<div class="filters" data-testid="diag-filters">
  <label>
    Target type
    <select bind:value={targetType} data-testid="diag-target-type">
      <option value="">all</option>
      {#each TARGET_TYPES as t}
        <option value={t}>{t}</option>
      {/each}
    </select>
  </label>

  <label for="diag-target-ac">
    Target id
    <Autocomplete
      id="diag-target-ac"
      items={deviceItems}
      bind:value={targetId}
      placeholder="device label or user id"
      testid="diag-target-id"
      on:change={apply}
    />
  </label>

  <label>
    Level
    <select bind:value={level} data-testid="diag-level">
      <option value="">error,warn (default)</option>
      <option value="all">all</option>
      <option value="error">error</option>
      <option value="warn">warn</option>
      <option value="info">info</option>
      <option value="debug">debug</option>
      <option value="trace">trace</option>
    </select>
  </label>

  <label>
    Search
    <input
      type="text"
      bind:value={q}
      placeholder="full-text"
      data-testid="diag-q"
    />
  </label>

  <label>
    Session id
    <input
      type="text"
      bind:value={sessionId}
      placeholder="correlation id"
      data-testid="diag-session-id"
    />
  </label>

  <label>
    From
    <input type="datetime-local" bind:value={fromDt} data-testid="diag-from" />
  </label>

  <label>
    To
    <input type="datetime-local" bind:value={toDt} data-testid="diag-to" />
  </label>

  <label>
    Limit
    <select bind:value={limit} data-testid="diag-limit">
      <option value={50}>50</option>
      <option value={100}>100</option>
      <option value={200}>200</option>
      <option value={500}>500</option>
    </select>
  </label>

  <button on:click={apply} data-testid="diag-apply">Apply</button>
  <button on:click={reset} data-testid="diag-reset">Reset</button>

  <label class="toggle">
    <input type="checkbox" bind:checked={autoRefresh} data-testid="diag-auto" />
    Auto-refresh (8s)
  </label>
</div>

{#if summary}
  <div class="summary" data-testid="diag-summary">
    <span class="pill pill-error">errors: {summary.totals.error}</span>
    <span class="pill pill-warn">warns: {summary.totals.warn}</span>
    {#if summary.top_messages.length > 0}
      <span class="top-label">Top:</span>
      {#each summary.top_messages.slice(0, 3) as m (m.msg)}
        <span class="top-msg" title={m.msg}>{truncate(m.msg, 60)} ({m.count})</span>
      {/each}
    {/if}
  </div>
{/if}

{#if error}
  <p class="error" data-testid="diag-error">{error}</p>
{/if}

<table data-testid="diag-table">
  <thead>
    <tr>
      <th>time</th>
      <th>target</th>
      <th>level</th>
      <th>src/logger</th>
      <th>msg</th>
    </tr>
  </thead>
  <tbody>
    {#each entries as e (e.id)}
      <tr
        class="row"
        on:click={() => toggleRow(e.id)}
        data-testid="diag-row-{e.id}"
      >
        <td class="mono">{fmtTs(e.ts)}</td>
        <td>
          {#if isUserTarget(e)}
            <UserLink id={e.target_id ?? ""} size={20} />
          {:else}{targetLabel(e)}{/if}
          <button
            type="button"
            class="filter-icon"
            aria-label="Filter by this target"
            on:click|stopPropagation={() => filterTarget(e)}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" /></svg>
          </button>
        </td>
        <td><span class={levelClass(e.level)}>{e.level}</span></td>
        <td class="mono">{e.src ?? e.logger ?? "—"}</td>
        <td>{truncate(e.msg)}</td>
      </tr>
      {#if expanded[e.id]}
        <tr class="detail" data-testid="diag-detail-{e.id}">
          <td colspan="5">
            <div class="detail-body">
              <div><strong>msg:</strong> <span class="mono">{e.msg}</span></div>
              {#if e.url}
                <div><strong>url:</strong> <span class="mono">{e.url}</span></div>
              {/if}
              {#if e.session_id}
                <div><strong>session:</strong> <span class="mono">{e.session_id}</span></div>
              {/if}
              {#if e.stack}
                <div><strong>stack:</strong></div>
                <pre>{e.stack}</pre>
              {/if}
              {#if e.extra !== null && e.extra !== undefined}
                <div><strong>extra:</strong></div>
                <pre>{prettyExtra(e.extra)}</pre>
              {/if}
            </div>
          </td>
        </tr>
      {/if}
    {/each}
    {#if entries.length === 0 && !loading}
      <tr><td colspan="5" class="empty" data-testid="diag-empty">No entries.</td></tr>
    {/if}
  </tbody>
</table>

<div class="footer">
  <button
    on:click={loadMore}
    disabled={nextCursor === null || loading}
    data-testid="diag-load-more"
  >
    {loading ? "Loading…" : "Load more"}
  </button>
  <span class="count" data-testid="diag-count">{entries.length} shown</span>
</div>

<style>
  .filters {
    display: flex;
    gap: 0.75rem;
    align-items: end;
    flex-wrap: wrap;
    margin-bottom: 0.75rem;
    padding: 0.75rem 1rem;
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 8px;
  }
  label {
    display: grid;
    gap: 0.2rem;
    font-size: 0.85rem;
    color: #d1d5db;
  }
  label.toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    align-self: center;
  }
  input,
  select {
    padding: 0.4rem 0.5rem;
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #1f2937;
    border-radius: 4px;
  }
  button {
    padding: 0.4rem 0.7rem;
    background: #1f2937;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 4px;
    align-self: center;
    cursor: pointer;
  }
  button:disabled {
    opacity: 0.5;
    cursor: default;
  }
  .summary {
    display: flex;
    gap: 0.6rem;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 0.6rem;
    font-size: 0.82rem;
    color: #9ca3af;
  }
  .pill {
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    font-weight: 600;
  }
  .pill-error {
    background: #3f1d1d;
    color: #fca5a5;
  }
  .pill-warn {
    background: #3a2c0a;
    color: #fbbf24;
  }
  .top-label {
    color: #6b7280;
  }
  .top-msg {
    color: #9ca3af;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    background: #111827;
  }
  th,
  td {
    padding: 0.4rem 0.6rem;
    text-align: left;
    border-bottom: 1px solid #1f2937;
    font-size: 0.85rem;
    vertical-align: top;
  }
  th {
    background: #0f172a;
    color: #9ca3af;
  }
  tr.row {
    cursor: pointer;
  }
  tr.row:hover {
    background: #0f172a;
  }
  .mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.8rem;
    color: #93c5fd;
  }
  .lvl-error {
    color: #f87171;
    font-weight: 600;
  }
  .lvl-warn {
    color: #fbbf24;
    font-weight: 600;
  }
  .lvl-muted {
    color: #9ca3af;
  }
  .filter-icon {
    background: transparent;
    border: none;
    padding: 0 0.25rem;
    color: currentColor;
    cursor: pointer;
    vertical-align: middle;
    line-height: 0;
  }
  tr.detail td {
    background: #0b1220;
  }
  .detail-body {
    display: grid;
    gap: 0.3rem;
    font-size: 0.82rem;
    color: #d1d5db;
  }
  .detail-body strong {
    color: #9ca3af;
  }
  pre {
    margin: 0;
    padding: 0.5rem 0.6rem;
    background: #0f172a;
    border: 1px solid #1f2937;
    border-radius: 4px;
    overflow-x: auto;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 0.78rem;
    color: #cbd5e1;
  }
  .footer {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    margin-top: 0.6rem;
  }
  .count {
    color: #9ca3af;
    font-size: 0.82rem;
  }
  .empty {
    color: #6b7280;
    text-align: center;
  }
  .error {
    color: #fca5a5;
  }
</style>
